import React, { useState, useEffect } from 'react';
import {
  Typography,
  Button,
  Skeleton,
  Box,
  Chip,
  Modal,
  TextField,
  Paper,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Plus, Eye, Trash2, Clock, CheckCircle, Package, Barcode } from 'lucide-react';
import toast from 'react-hot-toast';

const InventoryCountSessions = () => {
  const [sessions, setSessions] = useState([]);
  const [open, setOpen] = useState(false);
  const [newSessionName, setNewSessionName] = useState('');
  const [loading, setLoading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) { navigate("/login"); return; }

    axios.get("https://shopify-inv-tracker-backend-d0b1c1125141.herokuapp.com/api/auth/profile", {
      headers: { Authorization: `Bearer ${token}` }, withCredentials: true,
    }).catch(() => navigate("/login"));

    const fetchSessions = async () => {
      setLoading(true);
      try {
        const res = await axios.get("https://shopify-inv-tracker-backend-d0b1c1125141.herokuapp.com/api/inventory-counts");
        setSessions(res.data);
      } catch (err) {
        console.error("Failed to fetch sessions:", err);
        toast.error('Failed to load sessions');
      }
      finally { setLoading(false); }
    };
    fetchSessions();
  }, [navigate]);

  const createNewSession = async () => {
    if (!newSessionName.trim()) return;
    const t = toast.loading('Creating session…');
    try {
      const res = await axios.post("https://shopify-inv-tracker-backend-d0b1c1125141.herokuapp.com/api/inventory-counts", {
        sessionName: newSessionName.trim()
      });
      setSessions(prev => [res.data, ...prev]);
      toast.success('Session created', { id: t });
      setOpen(false);
      setNewSessionName('');
      navigate(`/inventory-count/${res.data._id}`);
    } catch (err) {
      console.error("Failed to create session:", err);
      toast.error('Failed to create session', { id: t });
    }
  };

  const deleteSession = async () => {
    const t = toast.loading('Deleting session…');
    try {
      await axios.delete(`https://shopify-inv-tracker-backend-d0b1c1125141.herokuapp.com/api/inventory-counts/${sessionToDelete}`);
      setSessions(prev => prev.filter(s => s._id !== sessionToDelete));
      toast.success('Session deleted', { id: t });
    } catch (err) {
      console.error("Failed to delete session:", err);
      toast.error('Failed to delete session', { id: t });
    }
    finally { setDeleteModalOpen(false); setSessionToDelete(null); }
  };

  const getStatusProps = (status) => {
    switch (status) {
      case 'submitted': return { label: 'Submitted', color: '#10b981', icon: <CheckCircle size={12} /> };
      case 'finished': return { label: 'Finished', color: '#2383e2', icon: <Package size={12} /> };
      default: return { label: 'In Progress', color: '#f59e0b', icon: <Clock size={12} /> };
    }
  };

  return (
    <Box className="fade-in">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: '-0.02em', mb: 0.5 }}>
            Inventory Count
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Manage inventory counting sessions
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="contained" startIcon={<Plus size={16} />} onClick={() => setOpen(true)}
            sx={{ bgcolor: '#2383e2', '&:hover': { bgcolor: '#1b6ec2' } }}>
            New Session
          </Button>
          <Button variant="outlined" startIcon={<Barcode size={16} />} onClick={() => navigate('/inventory-count/barcodes')}>
            Barcodes
          </Button>
        </Box>
      </Box>

      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
        {/* Header */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: '1.5fr 100px 110px 110px 140px',
          px: 2, py: 1.25,
          bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(55,53,47,0.024)',
          borderBottom: '1px solid', borderColor: 'divider',
        }}>
          {['Session', 'Status', 'Created', 'Submitted', 'Actions'].map(h => (
            <Typography key={h} variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'text.secondary', fontSize: '0.7rem' }}>
              {h}
            </Typography>
          ))}
        </Box>

        {loading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <Box
              key={`sk-${i}`}
              sx={{
                display: 'grid',
                gridTemplateColumns: '1.5fr 100px 110px 110px 140px',
                px: 2, py: 1.75,
                borderBottom: '1px solid', borderColor: 'divider',
                alignItems: 'center',
              }}
            >
              <Skeleton variant="text" width="60%" />
              <Skeleton variant="rectangular" width={70} height={20} sx={{ borderRadius: 2 }} />
              <Skeleton variant="text" width={70} />
              <Skeleton variant="text" width={70} />
              <Skeleton variant="rectangular" width={110} height={22} sx={{ borderRadius: 1 }} />
            </Box>
          ))
        ) : sessions.length > 0 ? sessions.map(session => {
          const sp = getStatusProps(session.status);
          return (
            <Box key={session._id} sx={{
              display: 'grid',
              gridTemplateColumns: '1.5fr 100px 110px 110px 140px',
              px: 2, py: 1.5,
              borderBottom: '1px solid', borderColor: 'divider',
              '&:hover': { bgcolor: 'action.hover' },
              '&:last-child': { borderBottom: 'none' },
              alignItems: 'center',
            }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>{session.sessionName}</Typography>
              <Box>
                <Chip size="small" icon={sp.icon} label={sp.label}
                  sx={{
                    height: 22, fontSize: '0.65rem', fontWeight: 500,
                    bgcolor: `${sp.color}15`, color: sp.color, border: `1px solid ${sp.color}30`,
                    '& .MuiChip-icon': { color: sp.color },
                  }}
                />
              </Box>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {session.createdAt ? new Date(session.createdAt).toLocaleDateString() : '-'}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {session.submittedAt ? new Date(session.submittedAt).toLocaleDateString() : '-'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Button size="small" startIcon={<Eye size={14} />} onClick={() => navigate(`/inventory-count/${session._id}`)}
                  sx={{ fontSize: '0.7rem', minWidth: 'auto' }}>View</Button>
                <Button size="small" color="error" startIcon={<Trash2 size={14} />}
                  onClick={() => { setSessionToDelete(session._id); setDeleteModalOpen(true); }}
                  sx={{ fontSize: '0.7rem', minWidth: 'auto' }}>Delete</Button>
              </Box>
            </Box>
          );
        }) : (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">No sessions found.</Typography>
          </Box>
        )}
      </Box>

      {/* Create Modal */}
      <Modal open={open} onClose={() => setOpen(false)}>
        <Paper sx={{ p: 3, width: 360, mx: 'auto', mt: '20vh', borderRadius: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>New Session</Typography>
          <TextField label="Session Name" fullWidth size="small" value={newSessionName}
            onChange={e => setNewSessionName(e.target.value)} sx={{ mb: 2 }} />
          <Button fullWidth variant="contained" onClick={createNewSession}
            sx={{ bgcolor: '#2383e2', '&:hover': { bgcolor: '#1b6ec2' } }}>
            Create
          </Button>
        </Paper>
      </Modal>

      {/* Delete Modal */}
      <Modal open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
        <Paper sx={{ p: 3, width: 360, mx: 'auto', mt: '20vh', borderRadius: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>Delete Session</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
            Are you sure you want to delete this session?
          </Typography>
          <Button fullWidth variant="contained" color="error" onClick={deleteSession} sx={{ mb: 1 }}>Delete</Button>
          <Button fullWidth variant="outlined" onClick={() => setDeleteModalOpen(false)}>Cancel</Button>
        </Paper>
      </Modal>
    </Box>
  );
};

export default InventoryCountSessions;
